import type { CalendarDate } from '@internationalized/date';
import { toUTCEpochEndOfDay, toUTCEpochStartOfDay, unixTimestampToCalendarDate } from '@/utils/time';

const SECONDS_PER_DAY = 86_400;

export interface RangeToTimestampsOptions {
  /** "Now" — used when the end is today, and to derive today's date. */
  now?: Date;
  /** Max span in days; longer ranges clamp the start forward. Default 365. */
  maxDays?: number;
}

/**
 * A selected calendar range → the deliveries query's inclusive unix-second
 * bounds: start-of-day for the start; end-of-day (23:59:59) for the end, or the
 * current timestamp when the end is today (so a range ending today includes
 * deliveries from earlier today); the span clamps to `maxDays` (the query
 * rejects ranges > 365 days). UTC day boundaries, via the shared `@/utils/time`
 * converters — matching the message-library range filter.
 */
export function rangeToTimestamps(
  range: { start: CalendarDate; end: CalendarDate },
  { now = new Date(), maxDays = 365 }: RangeToTimestampsOptions = {},
): { startTs: number; endTs: number } {
  const nowUnix = Math.floor(now.getTime() / 1000);
  const today = unixTimestampToCalendarDate(nowUnix);

  const startTs = toUTCEpochStartOfDay(range.start);
  const endTs = range.end.compare(today) === 0 ? nowUnix : toUTCEpochEndOfDay(range.end);

  const maxSeconds = maxDays * SECONDS_PER_DAY;
  return { startTs: endTs - startTs > maxSeconds ? endTs - maxSeconds : startTs, endTs };
}
